/**
 * 我的买卖 — single card (ys142857 parity).
 */
import { OTC_COLORS as C } from '@/constants';
import { tokenDisplayLabel } from '@/utils/orb-display';
import { ThemedAvatar } from '../ui/ThemedSurface';
import type { OtcMyTradeRow } from '../../api/otc';

function fmtNum(v: string, digits = 0): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

function fmtLegacyTime(value: string): string {
  const s = value.trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return s;
  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return s.replace('T', ' ').slice(0, 19);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
}

export function MyOtcTradeCard({
  row,
  onClick,
  onPointerDown,
}: {
  row: OtcMyTradeRow;
  onClick?: () => void;
  onPointerDown?: () => void;
}) {
  const isSell = row.side === 'sell';
  const statusLabel = row.status === 'done' ? '交易完成' : row.status_zh || row.status;
  const time = fmtLegacyTime(row.create_time);

  if (!isSell) {
    const title = row.trade_kind_zh || row.player_number || '指导购买';
    return (
      <button
        type="button"
        onClick={onClick}
        onPointerDown={onPointerDown}
        className="block w-full border-b px-4 py-4 text-left transition-opacity active:opacity-80"
        style={{ borderColor: C.line, background: C.pageBg }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <ThemedAvatar src={row.avatar} seed={row.player_number || title} className="h-9 w-9 shrink-0" />
            <span className="truncate text-sm font-semibold" style={{ color: C.text }}>
              {title}
            </span>
          </div>
          <span
            className="shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold"
            style={{
              borderColor: `${C.gold}99`,
              color: C.gold,
              background: `${C.gold}14`,
            }}
          >
            {statusLabel}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="font-mono text-base font-semibold" style={{ color: C.gold }}>
              {fmtNum(row.amount_uo, 0)}
            </div>
            <div className="mt-1 text-[11px]" style={{ color: C.muted }}>
              购买数量
            </div>
          </div>
          <div>
            <div className="font-mono text-base font-semibold" style={{ color: C.gold }}>
              {fmtNum(row.unit_price, 3)}
            </div>
            <div className="mt-1 text-[11px]" style={{ color: C.muted }}>
              币值
            </div>
          </div>
          <div>
            <div className="font-mono text-base font-semibold" style={{ color: C.gold }}>
              {fmtNum(row.ap_deal, 2)}
            </div>
            <div className="mt-1 text-[11px]" style={{ color: C.muted }}>
              消耗AP
            </div>
          </div>
        </div>

        <div className="mt-3 text-right text-[11px]" style={{ color: C.muted }}>
          {time}
        </div>
      </button>
    );
  }

  const qtyLabel = '挂卖数量';

  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={onPointerDown}
      className="block w-full border-b px-4 py-4 text-left transition-opacity active:opacity-80"
      style={{ borderColor: C.line, background: C.pageBg }}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-sm font-semibold" style={{ color: C.text }}>
          {row.trade_kind_zh || '指导销售'}
        </span>
        <span
          className="shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold"
          style={{
            borderColor: `${C.gold}99`,
            color: C.gold,
            background: `${C.gold}14`,
          }}
        >
          {statusLabel}
        </span>
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xl font-semibold leading-none" style={{ color: C.gold }}>
            {row.player_number || '—'}
          </div>
          <div className="mt-1 text-[11px]" style={{ color: C.muted }}>
            账户
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-baseline justify-end gap-1">
            <span className="font-mono text-xl font-semibold leading-none" style={{ color: C.gold }}>
              {fmtNum(row.amount_uo, 0)}
            </span>
            <span className="text-xs font-semibold" style={{ color: C.gold }}>
              {tokenDisplayLabel('uo')}
            </span>
          </div>
          <div className="mt-1 text-[11px]" style={{ color: C.muted }}>
            {qtyLabel}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        {row.guide_sales_label ? (
          <div className="text-xs" style={{ color: C.text }}>
            {row.guide_sales_label}
          </div>
        ) : (
          <span />
        )}
        <div className="shrink-0 text-right text-[11px]" style={{ color: C.muted }}>
          {time}
        </div>
      </div>
    </button>
  );
}
